import { useEffect, useState } from "react";
import { api, img } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ArtworkCard from "@/components/ArtworkCard";
import { Layers, Star, Gavel, Users, Image as ImageIcon, Gift, ArrowRight, CheckCircle2, CalendarClock, Sparkles, Clock, Megaphone, Banknote, UserPlus, ListChecks, Trophy, Crown } from "lucide-react";
import { motion } from "framer-motion";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const steps = [
  { icon: ImageIcon, title: "Explore the Gallery", desc: "Browse ~1,000 public-domain masterpieces and buy your first artwork." },
  { icon: Star, title: "Complete Daily Reviews", desc: "Review any 2 gallery artworks for 30 seconds each, rate them, and earn ₦150 per review." },
  { icon: Gavel, title: "Join Wednesday Auctions", desc: "Own at least one artwork and wait 7 days to list in weekly auctions." },
  { icon: Users, title: "Refer & Earn", desc: "Share your referral code and earn 3% on your downline's purchases." },
];

function countdown(target) {
  const ms = new Date(target) - new Date();
  if (ms <= 0) return "now";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [review, setReview] = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [auctionCfg, setAuctionCfg] = useState(null);
  const [holdingsCount, setHoldingsCount] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [referral, setReferral] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [ticker, setTicker] = useState(null);
  const [winners, setWinners] = useState(null);
  const [security, setSecurity] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    api.get("/reviews/status").then((r) => setReview(r.data));
    api.get("/auctions").then((r) => setAuctions(r.data.items.filter((a) => a.status === "live").slice(0, 3)));
    api.get("/auctions/config").then((r) => setAuctionCfg(r.data)).catch(() => {});
    api.get("/holdings").then((r) => setHoldingsCount(r.data.count));
    api.get("/referrals").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/gallery/tier-mix").then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.items)).catch(() => {});
    api.get("/stats/ticker").then((r) => setTicker(r.data)).catch(() => {});
    api.get("/stats/weekly-winners").then((r) => setWinners(r.data)).catch(() => {});
    api.get("/account/security-status").then((r) => setSecurity(r.data)).catch(() => {});
    if (user && user.role !== "trial" && !user.onboarded && !sessionStorage.getItem(`arc_onboard_shown_${user.id}`)) {
      setShowOnboarding(true);
      sessionStorage.setItem(`arc_onboard_shown_${user.id}`, "1");
    }
  }, [user]);

  // Refresh the live ticker every 5 minutes so it feels real-time.
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/stats/ticker").then((r) => setTicker(r.data)).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const finishOnboarding = async () => {
    setShowOnboarding(false);
    try { await api.post("/onboarding/complete"); await refresh(); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold">{user?.role === "trial" ? "Free Trial" : `Member ${user?.member_id}`}</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold mt-1">Welcome, {cap(user?.username)}.</h1>
        <p className="text-sm text-muted-foreground mt-1">{user?.role === "trial" ? "Earn ₦100 per daily review during your trial. Activate membership to withdraw and unlock everything." : "Collect, review daily and make money from auction sales!"}</p>
      </div>

      {ticker && <StatsTicker t={ticker} />}

      {winners && (winners.biggest_sale || winners.top_earner || winners.top_collector_week) && (
        <WeeklyWinners w={winners} />
      )}

      {announcements.length > 0 && (
        <div className="bg-gold/10 border border-gold/30 rounded-3xl p-5 flex items-start gap-3" data-testid="announcement-banner">
          <Megaphone className="h-5 w-5 text-gold mt-0.5 shrink-0" />
          <div>
            <p className="font-heading font-bold">{announcements[0].title}</p>
            <p className="text-sm text-muted-foreground">{announcements[0].body}</p>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Layers} label="My Arts" value={holdingsCount} onClick={() => nav("/holdings")} testid="stat-holdings" />
        <StatCard icon={Star} label="Reviews left today" value={review ? review.remaining : "—"} onClick={() => nav("/review")} testid="stat-reviews" />
        <StatCard icon={Gavel} label="Live Auctions" value={auctions.length} onClick={() => nav("/auctions")} testid="stat-auctions" />
        <StatCard icon={Users} label="Referral Commission" value="3%" onClick={() => nav("/referrals")} testid="stat-referrals" small />
      </div>

      {/* Auction countdown + Daily review CTA */}
      <div className="grid lg:grid-cols-2 gap-4">
        {auctionCfg && (
          <div className="bg-primary text-primary-foreground rounded-3xl p-6 relative overflow-hidden" data-testid="auction-countdown">
            <div className="absolute -top-10 -right-10 h-40 w-40 navy-gradient opacity-40 blur-2xl rounded-full" />
            <div className="flex items-center gap-2 text-primary-foreground/70 text-sm relative"><CalendarClock className="h-4 w-4" /> Wednesday Auction</div>
            {auctionCfg.window_open ? (
              <>
                <p className="font-heading text-3xl font-bold mt-2 relative text-gold">Auction is LIVE</p>
                <p className="text-sm text-primary-foreground/70 mt-1 relative">List your eligible holdings now.</p>
              </>
            ) : (
              <>
                <p className="font-heading text-3xl font-bold mt-2 relative">Opens in {countdown(auctionCfg.next_window)}</p>
                <p className="text-sm text-primary-foreground/70 mt-1 relative">{new Date(auctionCfg.next_window).toLocaleString("en-NG", { weekday: "long", hour: "2-digit", minute: "2-digit" })} (WAT)</p>
              </>
            )}
            <Button size="sm" variant="secondary" className="rounded-full mt-4 relative" onClick={() => nav("/auctions")} data-testid="goto-auctions-btn">View Auctions <ArrowRight className="h-3 w-3 ml-1" /></Button>
          </div>
        )}

        <div className="bg-card border border-border rounded-3xl p-6 flex flex-col justify-between">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl navy-gradient grid place-items-center shrink-0"><Star className="h-6 w-6 text-white" /></div>
            <div>
              <h3 className="font-heading text-xl font-bold">Daily Review Task</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {review?.owns_artwork
                  ? `Earn ₦${review?.reward_per_review} per review • ${review?.remaining} of ${review?.max_daily} left today`
                  : "Own at least one artwork to unlock daily rewarded reviews."}
              </p>
            </div>
          </div>
          <Button className="rounded-full h-11 px-6 mt-4 navy-gradient text-white font-semibold self-start" onClick={() => nav(review?.owns_artwork ? "/review" : "/gallery")} data-testid="daily-review-cta">
            {review?.owns_artwork ? "Start Review" : "Buy Artwork"}
          </Button>
        </div>
      </div>

      {/* Referral progress */}
      {referral && (
        <div className="bg-card border border-border rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="referral-progress">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-gold/15 grid place-items-center shrink-0"><Gift className="h-6 w-6 text-gold" /></div>
            <div>
              <h3 className="font-heading text-xl font-bold">Refer & Earn {referral.commission_pct ?? 3}%</h3>
              <p className="text-sm text-muted-foreground mt-0.5">{referral.count} referral{referral.count !== 1 ? "s" : ""} • {referral.purchased_count} purchased • earned ₦{Number(referral.total_earned || 0).toLocaleString()}</p>
            </div>
          </div>
          <Button variant="outline" className="rounded-full h-11 px-6" onClick={() => nav("/referrals")} data-testid="referral-cta">Share your code</Button>
        </div>
      )}

      {/* Featured collection (mixed tiers) */}
      {featured.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold" /> Featured Collection</h3>
            <button onClick={() => nav("/gallery")} className="text-sm text-gold font-semibold">Browse all</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="featured-collection">
            {featured.slice(0, 4).map((art, i) => <ArtworkCard key={art.id} art={art} index={i} />)}
          </div>
        </div>
      )}

      {/* Auction highlights */}
      {auctions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-xl font-bold">Live Auctions</h3>
            <button onClick={() => nav("/auctions")} className="text-sm text-gold font-semibold">View all</button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {auctions.map((a) => (
              <div key={a.id} onClick={() => nav(`/auctions/${a.id}`)} className="bg-card border border-border rounded-2xl overflow-hidden cursor-pointer group" data-testid={`dash-auction-${a.id}`}>
                <div className="aspect-video overflow-hidden bg-muted"><img src={img(a.image)} alt={a.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform" /></div>
                <div className="p-4">
                  <p className="font-heading font-semibold truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Top bid</p>
                  <p className="font-bold text-gold">₦{Number(a.current_bid).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showOnboarding} onOpenChange={(o) => { if (!o) finishOnboarding(); }}>
        <DialogContent className="rounded-3xl max-w-lg">
          <div className="text-center">
            <div className="h-14 w-14 rounded-2xl navy-gradient grid place-items-center mx-auto"><Gift className="h-7 w-7 text-white" /></div>
            <DialogTitle className="font-heading text-2xl font-bold mt-4">Welcome to AR Club, {cap(user?.username)}!</DialogTitle>
            <DialogDescription>Here's how to make the most of your membership.</DialogDescription>
          </div>
          <div className="space-y-3 mt-4">
            {steps.map((s) => (
              <div key={s.title} className="flex items-start gap-3 bg-secondary rounded-2xl p-4">
                <s.icon className="h-5 w-5 text-gold mt-0.5 shrink-0" />
                <div><p className="font-semibold text-sm">{s.title}</p><p className="text-xs text-muted-foreground">{s.desc}</p></div>
              </div>
            ))}
          </div>
          <Button onClick={finishOnboarding} data-testid="finish-onboarding" className="w-full h-12 rounded-full navy-gradient text-white font-semibold mt-2">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Got it, let's go!
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReceivingAccountCard({ s, nav }) {
  const verified = s.bank_status === "VERIFIED";
  const b = s.bank_account;
  const steps = [
    { label: "Receiving account verified", done: verified },
    { label: "Withdrawal password created", done: s.has_withdrawal_password },
  ];
  const allDone = steps.every((x) => x.done);
  return (
    <div className="bg-card border border-border rounded-3xl p-5" data-testid="receiving-account-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-11 w-11 rounded-2xl grid place-items-center shrink-0 ${verified ? "bg-green-500/15" : "bg-gold/15"}`}>
            <Banknote className={`h-5 w-5 ${verified ? "text-green-600" : "text-gold"}`} />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold">Receiving Account</p>
            {verified && b ? (
              <p className="text-xs text-muted-foreground truncate" data-testid="receiving-account-masked">
                {b.bank_name} • {b.account_number_masked} • {b.account_name}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{s.pending_bank_change ? "Change pending admin approval" : "Not set up yet"}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${verified ? "bg-green-500/15 text-green-600" : "bg-gold/15 text-gold"}`} data-testid="receiving-account-status">
            {verified ? "Verified ✓" : (s.bank_status === "REQUIRES_UPDATE" ? "Update Pending" : "Setup Required")}
          </span>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => nav("/wallets")} data-testid="manage-receiving-account">
            {verified ? "Manage" : "Set Up"}
          </Button>
        </div>
      </div>
      {!allDone && (
        <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="onboarding-checklist">
          {steps.map((st) => (
            <div key={st.label} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className={`h-4 w-4 shrink-0 ${st.done ? "text-green-600" : "text-muted-foreground/40"}`} />
              <span className={st.done ? "text-muted-foreground line-through" : ""}>{st.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeeklyWinners({ w }) {
  const ngn = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
  return (
    <div className="rounded-3xl p-6 relative overflow-hidden bg-gradient-to-br from-[#0B1D51] to-[#12296b] text-white" data-testid="weekly-winners">
      <div className="absolute -top-12 -right-8 h-44 w-44 bg-gold/30 blur-3xl rounded-full" />
      <div className="flex items-center gap-2 relative">
        <Trophy className="h-5 w-5 text-gold" />
        <h3 className="font-heading text-xl font-bold">This Week's Winners</h3>
      </div>
      <p className="text-sm text-white/60 mt-0.5 relative">Highlights from the last 7 days on AR Club.</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-5 relative">
        {w.biggest_sale && (
          <div className="bg-white/10 rounded-2xl p-4" data-testid="winner-biggest-sale">
            <div className="flex items-center gap-1.5 text-gold text-xs font-bold uppercase tracking-wide"><Gavel className="h-3.5 w-3.5" /> Biggest Sale</div>
            <p className="font-heading text-2xl font-bold mt-1.5">{ngn(w.biggest_sale.price)}</p>
            <p className="text-sm text-white/70 truncate">{w.biggest_sale.title}</p>
            {w.biggest_sale.seller && <p className="text-xs text-white/50 mt-0.5 truncate">by @{w.biggest_sale.seller}</p>}
          </div>
        )}
        {w.top_earner && (
          <div className="bg-white/10 rounded-2xl p-4" data-testid="winner-top-earner">
            <div className="flex items-center gap-1.5 text-gold text-xs font-bold uppercase tracking-wide"><Crown className="h-3.5 w-3.5" /> Top Earner</div>
            <p className="font-heading text-2xl font-bold mt-1.5">@{w.top_earner.username}</p>
            <p className="text-sm text-white/70">{ngn(w.top_earner.total)} in sales</p>
          </div>
        )}
        {w.top_collector_week && (
          <div className="bg-white/10 rounded-2xl p-4" data-testid="winner-top-collector">
            <div className="flex items-center gap-1.5 text-gold text-xs font-bold uppercase tracking-wide"><Layers className="h-3.5 w-3.5" /> Most Collected</div>
            <p className="font-heading text-2xl font-bold mt-1.5">@{w.top_collector_week.username}</p>
            <p className="text-sm text-white/70">{w.top_collector_week.count} artwork{w.top_collector_week.count === 1 ? "" : "s"} this week</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, testid, small }) {
  return (
    <button onClick={onClick} data-testid={testid} className="bg-card border border-border rounded-3xl p-5 text-left hover:shadow-lg transition-shadow">
      <Icon className="h-5 w-5 text-gold" />
      <p className={`font-heading font-bold mt-3 ${small ? "text-xl" : "text-3xl"}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </button>
  );
}

function StatsTicker({ t }) {
  const ngn = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
  const stats = [
    { icon: Banknote, label: "Total Paid Out", value: ngn(t.total_paid_out) },
    { icon: UserPlus, label: "Newest Member", value: t.latest_member ? `@${t.latest_member}` : "—" },
    { icon: ListChecks, label: "Tasks Completed", value: Number(t.tasks_completed || 0).toLocaleString() },
    { icon: Users, label: "Active Members", value: Number(t.members || 0).toLocaleString() },
    { icon: Layers, label: "Artworks Collected", value: Number(t.artworks_collected || 0).toLocaleString() },
    { icon: Gavel, label: "Auctions Settled", value: Number(t.auctions_completed || 0).toLocaleString() },
  ];
  const activity = t.recent_activity || [];
  const Row = () => (
    <div className="inline-flex items-center">
      {activity.map((a, i) => (
        <span key={`a${i}`} className="inline-flex items-center gap-2 px-6">
          <span className="h-2 w-2 rounded-full bg-[#0b1d51] animate-pulse shrink-0" />
          <span className="text-base font-bold text-[#0b1d51]">{a}</span>
          <span className="text-[#0b1d51]/40 ml-4">•</span>
        </span>
      ))}
      {stats.map((it, i) => (
        <span key={`s${i}`} className="inline-flex items-center gap-2 px-6">
          <it.icon className="h-5 w-5 text-[#0b1d51] shrink-0" />
          <span className="text-sm text-[#0b1d51]/70">{it.label}:</span>
          <span className="text-lg font-extrabold text-[#0b1d51]">{it.value}</span>
          <span className="text-[#0b1d51]/40 ml-4">•</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="ticker-sky rounded-2xl overflow-hidden ticker-mask relative" data-testid="stats-ticker">
      <div className="py-3 relative z-[1]">
        <div className="ticker-track">
          <Row />
          <Row />
        </div>
      </div>
    </div>
  );
}
